import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Card, Alert } from '../components/ui';
import dataService from '../services/DataService';

/**
 * SettingsPage component for system settings and data management
 * @returns {React.ReactElement} Settings component
 */
const SettingsPage = () => {
  const { currentUser, hasPermission } = useAuth();
  const navigate = useNavigate();

  // State
  const [activeTab, setActiveTab] = useState('general');
  const [generalSettings, setGeneralSettings] = useState({
    clinicName: '国际中医针灸研究院调理体验中心',
    clinicPhone: '',
    clinicAddress: '',
    clinicEmail: '',
    workingHoursStart: '09:00',
    workingHoursEnd: '18:00',
    appointmentDuration: 60,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Check if user has admin permission
  const isAdmin = hasPermission('admin');

  // Fetch settings on component mount
  useEffect(() => {
    const fetchSettings = () => {
      try {
        // In a real app, this would come from an API
        const savedSettings = localStorage.getItem('systemSettings');
        if (savedSettings) {
          setGeneralSettings(JSON.parse(savedSettings));
        }
      } catch (err) {
        console.error('Error loading settings:', err);
      }
    };

    fetchSettings();
  }, []);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setGeneralSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Save settings
  const handleSaveSettings = () => {
    setIsLoading(true);
    setError(null);

    try {
      localStorage.setItem('systemSettings', JSON.stringify(generalSettings));
      setIsLoading(false);
      setSaveSuccess(true);

      // Reset success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (err) {
      setError('保存设置时出错');
      setIsLoading(false);
    }
  };

  // Handle data backup
  const handleExportData = () => {
    try {
      // Collect data from localStorage
      const dataToExport = {
        customers: JSON.parse(localStorage.getItem('customers') || '[]'),
        treatments: JSON.parse(localStorage.getItem('treatments') || '[]'),
        practitioners: JSON.parse(localStorage.getItem('practitioners') || '[]'),
        venues: JSON.parse(localStorage.getItem('venues') || '[]'),
        appointments: JSON.parse(localStorage.getItem('appointments') || '[]'),
        treatmentRecords: JSON.parse(localStorage.getItem('treatmentRecords') || '[]'),
        systemSettings: JSON.parse(localStorage.getItem('systemSettings') || '{}'),
      };

      // Convert to JSON and create download link
      const dataStr = JSON.stringify(dataToExport, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);

      // Create and trigger download
      const downloadLink = document.createElement('a');
      const date = new Date().toISOString().split('T')[0];
      downloadLink.download = `clinic-data-backup-${date}.json`;
      downloadLink.href = url;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (err) {
      setError('导出数据时出错');
      console.error('Error exporting data:', err);
    }
  };

  // Handle data import
  const handleImportData = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const importedData = JSON.parse(event.target.result);

        // Validate the data structure
        const requiredKeys = ['customers', 'treatments', 'practitioners', 'venues', 'appointments', 'treatmentRecords'];
        const hasAllKeys = requiredKeys.every(key => key in importedData);

        if (!hasAllKeys) {
          setError('导入的数据格式不正确');
          return;
        }

        // Save each data type to localStorage
        localStorage.setItem('customers', JSON.stringify(importedData.customers || []));
        localStorage.setItem('treatments', JSON.stringify(importedData.treatments || []));
        localStorage.setItem('practitioners', JSON.stringify(importedData.practitioners || []));
        localStorage.setItem('venues', JSON.stringify(importedData.venues || []));
        localStorage.setItem('appointments', JSON.stringify(importedData.appointments || []));
        localStorage.setItem('treatmentRecords', JSON.stringify(importedData.treatmentRecords || []));

        if (importedData.systemSettings) {
          localStorage.setItem('systemSettings', JSON.stringify(importedData.systemSettings));
          setGeneralSettings(importedData.systemSettings);
        }

        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } catch (err) {
        setError('导入数据时出错，请确保文件格式正确');
        console.error('Error importing data:', err);
      }
    };

    reader.readAsText(file);
    // Reset file input
    e.target.value = null;
  };

  // Handle generate sample data
  const handleGenerateSampleData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // First clear existing data
      dataService.clearAllData();

      // Then generate new sample data
      const result = dataService.generateSampleData();

      if (result.success) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);

        // Refresh the page to show new data
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        setError(result.error || '生成示例数据失败');
      }
    } catch (err) {
      setError('生成示例数据时出错');
      console.error('Error generating sample data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Tab data
  const tabs = [
    {
      key: 'general',
      label: '基本设置',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )
    },
    {
      key: 'data',
      label: '数据管理',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
        </svg>
      )
    },
    {
      key: 'users',
      label: '用户管理',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
        </svg>
      )
    }
  ];

  // Render tabs content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <Card variant="modern" padding="large">
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">诊所信息</h3>
                <p className="text-gray-600">
                  设置您的诊所基本信息
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Clinic Name */}
                <Input
                  label="诊所名称"
                  name="clinicName"
                  value={generalSettings.clinicName}
                  onChange={handleInputChange}
                  placeholder="请输入诊所名称"
                  variant="outlined"
                />

                {/* Clinic Phone */}
                <Input
                  label="诊所电话"
                  name="clinicPhone"
                  type="tel"
                  value={generalSettings.clinicPhone}
                  onChange={handleInputChange}
                  placeholder="请输入诊所电话"
                  variant="outlined"
                />

                {/* Clinic Address */}
                <div className="md:col-span-2">
                  <Input
                    label="诊所地址"
                    name="clinicAddress"
                    value={generalSettings.clinicAddress}
                    onChange={handleInputChange}
                    placeholder="请输入诊所地址"
                    variant="outlined"
                  />
                </div>

                {/* Clinic Email */}
                <Input
                  label="电子邮箱"
                  name="clinicEmail"
                  type="email"
                  value={generalSettings.clinicEmail}
                  onChange={handleInputChange}
                  placeholder="请输入电子邮箱"
                  variant="outlined"
                />
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">营业设置</h3>
                <p className="text-gray-600 mb-6">
                  设置您的营业时间和预约相关选项
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Working Hours */}
                  <Input
                    label="营业开始时间"
                    name="workingHoursStart"
                    type="time"
                    value={generalSettings.workingHoursStart}
                    onChange={handleInputChange}
                    variant="outlined"
                  />

                  <Input
                    label="营业结束时间"
                    name="workingHoursEnd"
                    type="time"
                    value={generalSettings.workingHoursEnd}
                    onChange={handleInputChange}
                    variant="outlined"
                  />

                  {/* Appointment Duration */}
                  <Input
                    label="默认预约时长（分钟）"
                    name="appointmentDuration"
                    type="number"
                    value={generalSettings.appointmentDuration}
                    onChange={handleInputChange}
                    placeholder="60"
                    variant="outlined"
                    min={15}
                    step={15}
                  />
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-6 border-t border-gray-200">
                <Button
                  onClick={handleSaveSettings}
                  variant="primary"
                  loading={isLoading}
                  size="medium"
                >
                  保存设置
                </Button>
              </div>
            </div>
          </Card>
        );

      case 'data':
        return (
          <div className="space-y-6">
            {/* Data Export */}
            <Card variant="modern" padding="large">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-emerald-100">
                    <svg className="h-6 w-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    数据导出
                  </h3>
                  <p className="text-gray-600 mb-4">
                    导出所有系统数据为JSON文件，用于备份或迁移数据。
                  </p>
                  <Button
                    onClick={handleExportData}
                    variant="primary"
                    size="medium"
                  >
                    导出数据
                  </Button>
                </div>
              </div>
            </Card>

            {/* Data Import */}
            <Card variant="modern" padding="large">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-blue-100">
                    <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    数据导入
                  </h3>
                  <p className="text-gray-600 mb-4">
                    导入先前导出的数据文件，将覆盖系统中的现有数据。
                  </p>
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer"
                  >
                    <Button
                      variant="secondary"
                      size="medium"
                      onClick={() => document.getElementById('file-upload').click()}
                    >
                      选择文件并导入
                    </Button>
                  </label>
                  <input
                    id="file-upload"
                    name="file-upload"
                    type="file"
                    accept=".json"
                    className="sr-only"
                    onChange={handleImportData}
                  />
                </div>
              </div>
            </Card>

            {/* Generate Sample Data */}
            <Card variant="modern" padding="large">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-purple-100">
                    <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    生成示例数据
                  </h3>
                  <p className="text-gray-600 mb-4">
                    生成包含客户、医师、治疗项目等完整的示例数据，用于测试和演示。将覆盖现有数据。
                  </p>
                  <Button
                    onClick={() => {
                      if (window.confirm('确定要生成示例数据吗？这将覆盖现有的所有数据！')) {
                        handleGenerateSampleData();
                      }
                    }}
                    variant="primary"
                    loading={isLoading}
                    size="medium"
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    生成示例数据
                  </Button>
                </div>
              </div>
            </Card>

            {/* Clear Data */}
            <Card variant="modern" padding="large">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-red-100">
                    <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    清除数据
                  </h3>
                  <p className="text-gray-600 mb-4">
                    此操作将清除所有系统数据。此操作无法撤销，请谨慎操作。
                  </p>
                  <Button
                    onClick={() => {
                      if (window.confirm('确定要清除所有数据吗？此操作无法撤销！')) {
                        // Clear all app data from localStorage
                        dataService.clearAllData();
                        setSaveSuccess(true);
                        setTimeout(() => setSaveSuccess(false), 3000);

                        // Refresh the page to show cleared data
                        setTimeout(() => {
                          window.location.reload();
                        }, 1000);
                      }
                    }}
                    variant="danger"
                    size="medium"
                  >
                    清除所有数据
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        );

      case 'users':
        return (
          <Card variant="modern" padding="large">
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">用户管理</h3>
                <p className="text-gray-600">
                  管理系统用户和权限
                </p>
              </div>

              {!isAdmin ? (
                <Alert
                  type="warning"
                  title="权限不足"
                >
                  您需要管理员权限才能管理用户。
                </Alert>
              ) : (
                <div className="space-y-4">
                  {/* Sample Users - in a real app would be loaded from API/localStorage */}
                  {[
                    { id: 1, name: '管理员', username: 'admin', role: 'admin', status: '活跃' },
                    { id: 2, name: '前台接待', username: 'receptionist', role: 'receptionist', status: '活跃' },
                    { id: 3, name: '调理师张三', username: 'practitioner1', role: 'practitioner', status: '活跃' }
                  ].map((user) => (
                    <Card key={user.id} variant="simple" padding="medium" hover className="border-l-4 border-l-emerald-500">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                              <svg className="h-6 w-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                          </div>
                          <div>
                            <p className="text-lg font-semibold text-gray-900">
                              {user.name}
                            </p>
                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                              <span>用户名：{user.username}</span>
                              <span>角色：
                                {user.role === 'admin' ? '管理员' :
                                  user.role === 'manager' ? '经理' :
                                    user.role === 'practitioner' ? '调理师' :
                                      user.role === 'receptionist' ? '前台' :
                                        user.role}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${user.status === '活跃' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                            {user.status}
                          </span>
                          <Button
                            variant="ghost"
                            size="small"
                            onClick={() => alert('此功能在演示版本中不可用')}
                          >
                            编辑
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}

                  <div className="pt-4">
                    <Button
                      onClick={() => alert('此功能在演示版本中不可用')}
                      variant="primary"
                      size="medium"
                      startIcon={
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                      }
                    >
                      添加用户
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Card>
        );

      default:
        return null;
    }
  };

  if (!currentUser || currentUser.role?.toLowerCase() !== 'admin') {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            系统设置
          </h1>
          <p className="text-gray-600">管理您的系统配置和数据</p>
        </div>

        {/* Success message */}
        {saveSuccess && (
          <div className="mb-6">
            <Alert
              type="success"
              title="成功"
              dismissible
              onDismiss={() => setSaveSuccess(false)}
            >
              操作成功完成
            </Alert>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="mb-6">
            <Alert
              type="error"
              title="错误"
              dismissible
              onDismiss={() => setError(null)}
            >
              {error}
            </Alert>
          </div>
        )}

        {/* Settings Navigation Tabs */}
        <Card variant="modern" padding="none" className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`${activeTab === tab.key
                    ? 'border-emerald-500 text-emerald-600 bg-emerald-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-3 border-b-2 font-medium text-sm flex items-center space-x-2 rounded-t-lg transition-all duration-200`}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </Card>

        {/* Tab Content */}
        <div>
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;